import { useEffect, useState, type ReactNode } from "react";
import { useRouter } from "expo-router";
import { useTranslation } from "react-i18next";
import {
  useEmployerApplicantDetail,
  useSendEmployerApplicantMessage,
  useUpdateEmployerApplicantStatus,
} from "../../hooks/useJobsApi";
import { useTalentSubmissionDetail } from "../../hooks/useApplicationsApi";
import { useLocale } from "../../hooks/useLocale";
import { useToast } from "../../hooks/useToast";
import { getEmployerApplicantNextStatuses } from "../../lib/employer-applicants";
import { formatRelativeDate } from "../../lib/jobs-format";
import { JobsSkeleton } from "./JobsSkeleton.web";
import { MaterialSymbol } from "../icons/MaterialSymbol";
import { SubmissionReceiptsSection } from "../applications/SubmissionReceiptsSection";
import {
  EmployerApplicantCvPaper,
  formatAbsoluteDate,
  getApplicantCvOptions,
  type ApplicantCvPaperDocument,
} from "./EmployerApplicantCvPaper.web";

/**
 * The applicant/talent detail body (Employer Phase 1 WP4).
 *
 * Extracted verbatim from `EmployerApplicantDetailScreen.web.tsx` so two
 * surfaces share one implementation:
 * - the per-job route keeps rendering it full-screen (`variant="page"`,
 *   zero visual change), and
 * - the company-wide Applications inbox renders it inside the master-detail
 *   right pane (`variant="pane"`, the pane owns the scroll).
 *
 * Pass `applicationId` (+ `jobId`) for a job application, or `submissionId`
 * for a talent-network submission. Talent submissions hide the job-only
 * status/message actions (PRD §9.4) and show consent/withdrawn state instead.
 */

export interface ApplicantDetailBodyProps {
  jobId: string | null;
  applicationId?: string;
  submissionId?: string;
  headerSlot?: ReactNode;
  onStatusChanged?: () => void;
  /** "page" = self-contained scrolling page (per-job route). "pane" = host owns scroll. */
  variant?: "page" | "pane";
}

function Frame({
  variant,
  state,
  children,
}: {
  variant: "page" | "pane";
  state: "loading" | "error" | "content";
  children: ReactNode;
}) {
  if (variant === "pane") {
    return (
      <div
        style={{
          fontFamily:
            "'Google Sans Text', 'Google Sans', Roboto, Arial, sans-serif",
        }}
      >
        {children}
      </div>
    );
  }

  if (state === "loading") {
    return (
      <div
        className="h-full overflow-y-auto"
        style={{
          minHeight: "80vh",
          backgroundColor: "var(--bg)",
        }}
      >
        {children}
      </div>
    );
  }

  if (state === "error") {
    return (
      <div
        className="h-full overflow-y-auto"
        style={{
          padding: "64px 32px 96px",
          minHeight: "80vh",
          backgroundColor: "var(--bg)",
          fontFamily:
            "'Google Sans Text', 'Google Sans', Roboto, Arial, sans-serif",
        }}
      >
        {children}
      </div>
    );
  }

  return (
    <div
      className="h-full overflow-y-auto"
      style={{
        backgroundColor: "var(--bg)",
        fontFamily:
          "'Google Sans Text', 'Google Sans', Roboto, Arial, sans-serif",
      }}
    >
      {children}
    </div>
  );
}

function Avatar({
  name,
  imageUrl,
  size = 64,
}: {
  name?: string | null;
  imageUrl?: string | null;
  size?: number;
}) {
  const [imageFailed, setImageFailed] = useState(false);

  useEffect(() => {
    setImageFailed(false);
  }, [imageUrl]);

  const initials =
    (name ?? "")
      .trim()
      .split(/\s+/)
      .filter(Boolean)
      .slice(0, 2)
      .map((part) => part.charAt(0).toUpperCase())
      .join("") || "?";

  const showImage = Boolean(imageUrl) && !imageFailed;

  return showImage ? (
    <img
      src={imageUrl ?? undefined}
      alt={name ?? "Applicant"}
      referrerPolicy="no-referrer"
      onError={() => setImageFailed(true)}
      style={{
        width: size,
        height: size,
        borderRadius: "50%",
        objectFit: "cover",
        flexShrink: 0,
      }}
    />
  ) : (
    <div
      aria-label={name ?? "Applicant"}
      style={{
        width: size,
        height: size,
        borderRadius: "50%",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        flexShrink: 0,
        background: "rgba(0,0,0,0.06)",
        color: "var(--text-secondary)",
        fontSize: Math.round(size * 0.32),
        fontWeight: 500,
      }}
    >
      {initials}
    </div>
  );
}

function SectionCard({
  title,
  subtitle,
  children,
}: {
  title: string;
  subtitle?: string | null;
  children: ReactNode;
}) {
  return (
    <section style={{ marginTop: 8 }}>
      <div style={{ padding: "0 8px", marginBottom: 12 }}>
        <div
          style={{
            fontSize: 18,
            fontWeight: 500,
            color: "var(--text-primary)",
          }}
        >
          {title}
        </div>
        {subtitle ? (
          <div
            style={{
              marginTop: 4,
              fontSize: 13,
              lineHeight: 1.5,
              color: "var(--text-tertiary)",
            }}
          >
            {subtitle}
          </div>
        ) : null}
      </div>
      {children}
    </section>
  );
}

function CenterState({
  title,
  subtitle,
  detail,
  actionLabel,
  onAction,
}: {
  title: string;
  subtitle: string;
  detail?: string | null;
  actionLabel?: string;
  onAction?: () => void;
}) {
  return (
    <div
      style={{
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        gap: 10,
        paddingBlock: 48,
      }}
    >
      <div
        style={{
          fontSize: 18,
          fontWeight: 500,
          color: "var(--text-primary)",
          textAlign: "center",
        }}
      >
        {title}
      </div>
      <div
        style={{
          maxWidth: 420,
          fontSize: 14,
          lineHeight: "21px",
          color: "var(--text-secondary)",
          textAlign: "center",
        }}
      >
        {subtitle}
      </div>
      {detail ? (
        <div
          style={{
            maxWidth: 480,
            fontSize: 12,
            lineHeight: "18px",
            color: "var(--text-tertiary)",
            textAlign: "center",
          }}
        >
          {detail}
        </div>
      ) : null}
      {actionLabel && onAction ? (
        <button
          type="button"
          onClick={onAction}
          style={{
            display: "inline-flex",
            alignItems: "center",
            justifyContent: "center",
            minHeight: 40,
            paddingInline: 18,
            borderRadius: 9999,
            border: 0,
            backgroundColor: "var(--text-primary)",
            color: "var(--bg)",
            fontSize: 14,
            fontWeight: 500,
            fontFamily: "inherit",
            cursor: "pointer",
            transition: "opacity 150ms",
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.opacity = "0.88";
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.opacity = "1";
          }}
        >
          {actionLabel}
        </button>
      ) : null}
    </div>
  );
}

function ActionButton({
  label,
  onClick,
  disabled,
  loading,
  variant = "solid",
  icon,
}: {
  label: string;
  onClick: () => void;
  disabled?: boolean;
  loading?: boolean;
  variant?: "solid" | "outline";
  icon?: ReactNode;
}) {
  const isDisabled = disabled || loading;

  if (variant === "outline") {
    return (
      <button
        type="button"
        onClick={onClick}
        disabled={isDisabled}
        className="tabbio-chip"
        style={{
          opacity: isDisabled ? 0.45 : 1,
          cursor: isDisabled ? "default" : "pointer",
        }}
      >
        {icon}
        <span>{label}</span>
      </button>
    );
  }

  return (
    <button
      type="button"
      onClick={onClick}
      disabled={isDisabled}
      style={{
        display: "inline-flex",
        alignItems: "center",
        justifyContent: "center",
        gap: 8,
        minHeight: 40,
        paddingInline: 18,
        borderRadius: 9999,
        border: 0,
        backgroundColor: "var(--text-primary)",
        color: "var(--bg)",
        fontSize: 14,
        fontWeight: 500,
        fontFamily: "inherit",
        cursor: isDisabled ? "default" : "pointer",
        opacity: isDisabled ? 0.45 : 1,
        transition: "opacity 150ms",
      }}
      onMouseEnter={(e) => {
        if (!isDisabled) e.currentTarget.style.opacity = "0.88";
      }}
      onMouseLeave={(e) => {
        if (!isDisabled) e.currentTarget.style.opacity = "1";
      }}
    >
      {icon}
      <span>{label}</span>
    </button>
  );
}

/**
 * `dir="auto"` is not enough for a phone number: "+971…" starts with a NEUTRAL
 * character, so auto resolves the run to the surrounding paragraph direction
 * and Arabic pages paint it as "971500000001+". Phone/handle/URL values need
 * an explicit "ltr"; prose keeps "auto".
 */
function InfoPill({
  icon,
  label,
  dir = 'auto',
}: {
  icon?: ReactNode;
  label: string;
  dir?: 'auto' | 'ltr';
}) {
  return (
    <div
      style={{
        display: "inline-flex",
        alignItems: "center",
        gap: 6,
        minHeight: 28,
        paddingInline: 10,
        borderRadius: 9999,
        background: "rgba(0,0,0,0.06)",
        color: "var(--text-secondary)",
        fontSize: 12,
        fontWeight: 500,
        lineHeight: "18px",
      }}
    >
      {icon}
      <span dir={dir}>{label}</span>
    </div>
  );
}

function StatusBadge({ label }: { label: string }) {
  return <span className="tabbio-menu-badge">{label}</span>;
}

function FactGrid({
  facts,
}: {
  facts: { label: string; value: string; dir?: 'auto' | 'ltr' }[];
}) {
  return (
    <div
      style={{
        display: "grid",
        gridTemplateColumns: "repeat(2, minmax(0, 1fr))",
        gap: 12,
      }}
    >
      {facts.map((fact) => (
        <div
          key={`${fact.label}:${fact.value}`}
          style={{
            minHeight: 78,
            display: "flex",
            flexDirection: "column",
            gap: 4,
            padding: 16,
            borderRadius: 20,
            background: "rgba(0,0,0,0.04)",
          }}
        >
          <div
            style={{
              fontSize: 13,
              fontWeight: 500,
              color: "var(--text-secondary)",
            }}
          >
            {fact.label}
          </div>
          <div
            style={{
              fontSize: 14,
              lineHeight: "20px",
              fontWeight: 600,
              color: "var(--text-primary)",
              wordBreak: "break-word",
            }}
            dir={fact.dir ?? 'auto'}
          >
            {fact.value}
          </div>
        </div>
      ))}
    </div>
  );
}

function StatusHistorySection({
  title,
  entries,
  isRTL,
  locale,
  byUserLabel,
  statusLabel,
}: {
  title: string;
  entries: { status: string; at: string; byUserId?: string | null }[];
  isRTL: boolean;
  locale: string;
  byUserLabel: (id: string) => string;
  statusLabel: (status: string) => string;
}) {
  if (!entries.length) return null;

  return (
    <SectionCard title={title}>
      <div style={{ display: "flex", flexDirection: "column", gap: 2 }}>
        {entries.map((entry, index) => (
          <div
            key={`${entry.status}:${entry.at}:${index}`}
            style={{
              display: "flex",
              alignItems: "center",
              gap: 12,
              padding: "12px 18px",
              borderRadius: 18,
              backgroundColor: "rgba(0,0,0,0.04)",
              textAlign: isRTL ? "right" : "left",
            }}
          >
            <div
              aria-hidden
              style={{
                width: 32,
                height: 32,
                borderRadius: "50%",
                background: "rgba(0,0,0,0.06)",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                color: "var(--text-secondary)",
                flexShrink: 0,
              }}
            >
              <MaterialSymbol name="history" size={18} />
            </div>
            <div
              style={{
                flex: 1,
                minWidth: 0,
                display: "flex",
                flexDirection: "column",
                gap: 2,
              }}
            >
              <div
                style={{
                  fontSize: 14,
                  fontWeight: 500,
                  color: "var(--text-primary)",
                }}
              >
                {statusLabel(entry.status)}
              </div>
              <div
                style={{
                  fontSize: 12,
                  lineHeight: "18px",
                  color: "var(--text-tertiary)",
                }}
              >
                {formatAbsoluteDate(entry.at, locale)}
                {entry.byUserId ? ` · ${byUserLabel(entry.byUserId)}` : ""}
              </div>
            </div>
          </div>
        ))}
      </div>
    </SectionCard>
  );
}

// ─── Application detail (moved from EmployerApplicantDetailScreen.web.tsx) ───

function ApplicationDetailBody({
  jobId,
  applicationId,
  headerSlot,
  onStatusChanged,
  variant,
}: {
  jobId: string;
  applicationId: string;
  headerSlot?: ReactNode;
  onStatusChanged?: () => void;
  variant: "page" | "pane";
}) {
  const { t, i18n } = useTranslation("common");
  const { isRTL } = useLocale();
  const router = useRouter();
  const toast = useToast();
  const [message, setMessage] = useState("");
  const [selectedCvId, setSelectedCvId] = useState<string | null>(null);

  const detailQuery = useEmployerApplicantDetail(
    jobId,
    applicationId,
    Boolean(jobId && applicationId),
  );
  const updateStatusMutation = useUpdateEmployerApplicantStatus(
    jobId,
    applicationId,
  );
  const sendMessageMutation = useSendEmployerApplicantMessage(
    jobId,
    applicationId,
  );

  const detail = detailQuery.data;
  const nextStatuses = detail
    ? getEmployerApplicantNextStatuses(detail.status)
    : [];
  const cvOptions = getApplicantCvOptions(detail ?? null);
  const selectedCv =
    cvOptions.find((cv) => cv.id === selectedCvId) ?? cvOptions[0] ?? null;
  const hasAuthError = /unauthorized/i.test(detailQuery.error?.message ?? "");

  useEffect(() => {
    if (!cvOptions.length) {
      setSelectedCvId(null);
      return;
    }

    setSelectedCvId((current) => {
      if (current && cvOptions.some((cv) => cv.id === current)) {
        return current;
      }

      return cvOptions[0].id;
    });
  }, [cvOptions]);

  const handleStatusChange = async (
    nextStatus: (typeof nextStatuses)[number],
  ) => {
    try {
      await updateStatusMutation.mutateAsync({ status: nextStatus });
      onStatusChanged?.();
      toast.success(
        t("jobApplicants.statusUpdatedTitle"),
        t("jobApplicants.statusUpdatedBody", {
          status: t(`jobApplicants.status.${nextStatus}` as never),
        }),
      );
    } catch (error) {
      toast.error(
        t("jobApplicants.statusUpdateFailedTitle"),
        error instanceof Error ? error.message : t("errors.generic"),
      );
    }
  };

  const handleSendMessage = async () => {
    if (!message.trim()) {
      return;
    }

    try {
      await sendMessageMutation.mutateAsync({ content: message.trim() });
      setMessage("");
      toast.success(
        t("jobApplicants.messageSentTitle"),
        t("jobApplicants.messageSentBody"),
      );
    } catch (error) {
      toast.error(
        t("jobApplicants.messageFailedTitle"),
        error instanceof Error ? error.message : t("errors.generic"),
      );
    }
  };

  if (detailQuery.isLoading) {
    return (
      <Frame variant={variant} state="loading">
        <JobsSkeleton variant="detail" />
      </Frame>
    );
  }

  if (detailQuery.error || !detail) {
    return (
      <Frame variant={variant} state="error">
        <div className="tabbio-svc-page">
          {headerSlot}
          <CenterState
            title={
              detailQuery.error
                ? t("jobApplicants.loadErrorTitle")
                : t("jobApplicants.notFoundTitle")
            }
            subtitle={
              detailQuery.error
                ? hasAuthError
                  ? t("jobApplicants.loadErrorSubtitle", {
                      defaultValue:
                        "Your company session may have expired. Refresh the page or sign in again.",
                    })
                  : t("jobApplicants.loadErrorSubtitle")
                : t("jobApplicants.notFoundSubtitle")
            }
            detail={detailQuery.error?.message ?? null}
            actionLabel={t("common:retry")}
            onAction={() => detailQuery.refetch()}
          />
        </div>
      </Frame>
    );
  }

  const factItems = [
    {
      label: t("jobApplicants.fields.job"),
      value: detail.job.title,
    },
    {
      label: t("jobApplicants.fields.company"),
      value: detail.company.name,
    },
    {
      label: t("jobApplicants.fields.appliedAt"),
      value: formatAbsoluteDate(detail.appliedAt, i18n.language),
    },
    {
      label: t("jobApplicants.fields.updatedAt"),
      value: formatAbsoluteDate(detail.updatedAt, i18n.language),
    },
  ];

  const contactItems = [
    {
      label: t("jobApplicants.fields.location"),
      value: detail.candidate.location || t("jobApplicants.noDataValue"),
    },
    {
      label: t("jobApplicants.fields.email"),
      value:
        detail.candidate.contactEmail ||
        detail.candidate.email ||
        t("jobApplicants.noDataValue"),
    },
    {
      label: t("jobApplicants.fields.phone"),
      value: detail.candidate.phone || t("jobApplicants.noDataValue"),
      dir: 'ltr' as const,
    },
    {
      label: t("jobApplicants.fields.nationality"),
      value: detail.candidate.nationality || t("jobApplicants.noDataValue"),
    },
  ];

  const profileSummary =
    detail.candidateProfile.about ||
    detail.candidateProfile.bio ||
    detail.coverLetter ||
    null;

  return (
    <Frame variant={variant} state="content">
      <div
        dir={isRTL ? "rtl" : "ltr"}
        className="tabbio-svc-page"
        style={{
          display: "flex",
          flexDirection: "column",
          gap: 32,
        }}
      >
        {headerSlot}

        {/* Hero: flat, no bordered card */}
        <header style={{ display: "flex", flexDirection: "column", gap: 18 }}>
          <div
            style={{
              display: "flex",
              alignItems: "flex-start",
              gap: 18,
              minWidth: 0,
            }}
          >
            <Avatar
              name={detail.candidate.name}
              imageUrl={detail.candidate.image}
              size={64}
            />

            <div
              style={{
                flex: 1,
                minWidth: 0,
                display: "flex",
                flexDirection: "column",
                gap: 8,
              }}
            >
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 8,
                  flexWrap: "wrap",
                }}
              >
                <StatusBadge
                  label={t(`jobApplicants.status.${detail.status}` as never)}
                />
                <InfoPill
                  icon={
                    <MaterialSymbol
                      name="schedule"
                      size={14}
                      style={{ color: "var(--text-tertiary)" }}
                    />
                  }
                  label={t("jobApplicants.appliedAgo", {
                    time: formatRelativeDate(detail.appliedAt, i18n.language),
                  })}
                />
              </div>

              <h1
                style={{
                  margin: 0,
                  fontFamily: "'Google Sans', sans-serif",
                  fontSize: 32,
                  lineHeight: "38px",
                  fontWeight: 400,
                  letterSpacing: "-0.01em",
                  color: "var(--text-primary)",
                }}
                dir="auto"
              >
                {detail.candidate.name}
              </h1>

              <div
                style={{
                  fontSize: 15,
                  lineHeight: "22px",
                  color: "var(--text-secondary)",
                }}
              >
                {[
                  detail.candidate.title || t("jobApplicants.noTitle"),
                  detail.job.title,
                  detail.company.name,
                ]
                  .filter(Boolean)
                  .join(" · ")}
              </div>
            </div>
          </div>

          {/* Contact pills */}
          <div
            style={{
              display: "flex",
              gap: 8,
              flexWrap: "wrap",
            }}
          >
            {detail.candidate.location ? (
              <InfoPill
                icon={
                  <MaterialSymbol
                    name="location_on"
                    size={14}
                    style={{ color: "var(--text-tertiary)" }}
                  />
                }
                label={detail.candidate.location}
              />
            ) : null}
            {detail.candidate.contactEmail || detail.candidate.email ? (
              <InfoPill
                icon={
                  <MaterialSymbol
                    name="mail"
                    size={14}
                    style={{ color: "var(--text-tertiary)" }}
                  />
                }
                label={detail.candidate.contactEmail || detail.candidate.email}
              />
            ) : null}
            {detail.candidate.phone ? (
              <InfoPill
                icon={
                  <MaterialSymbol
                    name="call"
                    size={14}
                    style={{ color: "var(--text-tertiary)" }}
                  />
                }
                label={detail.candidate.phone}
                dir="ltr"
              />
            ) : null}
            <InfoPill
              icon={
                <MaterialSymbol
                  name="work"
                  size={14}
                  style={{ color: "var(--text-tertiary)" }}
                />
              }
              label={detail.job.title}
            />
          </div>

          {/* Action row */}
          {nextStatuses.length || detail.conversationId ? (
            <div
              style={{
                display: "flex",
                gap: 8,
                flexWrap: "wrap",
                marginTop: 4,
              }}
            >
              {nextStatuses.map((status) => (
                <ActionButton
                  key={status}
                  label={t(`jobApplicants.actions.${status}` as never)}
                  onClick={() => handleStatusChange(status)}
                  disabled={updateStatusMutation.isPending}
                  loading={updateStatusMutation.isPending}
                  variant={status === "rejected" ? "outline" : "solid"}
                />
              ))}

              {detail.conversationId ? (
                <ActionButton
                  label={t("jobApplicants.actions.openThread")}
                  onClick={() =>
                    router.push(`/messages/${detail.conversationId}` as never)
                  }
                  variant="outline"
                  icon={
                    <MaterialSymbol
                      name={isRTL ? "arrow_back" : "arrow_forward"}
                      size={16}
                    />
                  }
                />
              ) : null}
            </div>
          ) : null}

          {profileSummary ? (
            <div
              style={{
                marginTop: 4,
                fontSize: 14,
                lineHeight: "22px",
                color: "var(--text-secondary)",
                whiteSpace: "pre-wrap",
                textAlign: "start",
              }}
            >
              {profileSummary}
            </div>
          ) : null}
        </header>

        {/* SmartCV */}
        <SectionCard title={t("jobApplicants.sections.smartCv")}>
          {cvOptions.length > 1 ? (
            <div
              style={{
                display: "flex",
                gap: 8,
                flexWrap: "wrap",
                marginBottom: 12,
              }}
            >
              {cvOptions.map((cv) => (
                <button
                  key={cv.id}
                  type="button"
                  onClick={() => setSelectedCvId(cv.id)}
                  className={
                    selectedCv?.id === cv.id
                      ? "tabbio-chip active"
                      : "tabbio-chip"
                  }
                >
                  {cv.isSubmitted ? t("jobApplicants.cv.submitted") : cv.title}
                </button>
              ))}
            </div>
          ) : null}

          <EmployerApplicantCvPaper
            detail={detail}
            cv={selectedCv}
            locale={i18n.language}
            isRTL={isRTL}
            hasAttachedCv={Boolean(detail.submittedCvDocument)}
          />
        </SectionCard>

        {/* Every submission of this application, as summaries (E03). */}
        <SubmissionReceiptsSection audience="employer" fields={detail} />

        {/* Screening answers */}
        {detail.screeningAnswers.length ? (
          <SectionCard title={t("jobApplicants.sections.screeningAnswers")}>
            <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
              {detail.screeningAnswers.map((answer) => (
                <div
                  key={answer.questionId}
                  style={{
                    display: "flex",
                    flexDirection: "column",
                    gap: 6,
                    padding: "16px 18px",
                    borderRadius: 20,
                    backgroundColor: "rgba(0,0,0,0.04)",
                    textAlign: "start",
                  }}
                >
                  <div
                    style={{
                      fontSize: 14,
                      fontWeight: 500,
                      lineHeight: "20px",
                      color: "var(--text-primary)",
                    }}
                  >
                    {answer.prompt}
                  </div>
                  <div
                    style={{
                      fontSize: 14,
                      lineHeight: "21px",
                      color: "var(--text-secondary)",
                      whiteSpace: "pre-wrap",
                    }}
                  >
                    {answer.answer}
                  </div>
                </div>
              ))}
            </div>
          </SectionCard>
        ) : null}

        {/* Send a message */}
        <SectionCard
          title={t("jobApplicants.sections.message")}
          subtitle={t("jobApplicants.messageHint")}
        >
          <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
            <textarea
              value={message}
              onChange={(event) => setMessage(event.target.value)}
              placeholder={t("jobApplicants.messagePlaceholder")}
              style={{
                minHeight: 148,
                width: "100%",
                resize: "vertical",
                padding: 16,
                borderRadius: 20,
                border: 0,
                backgroundColor: "rgba(0,0,0,0.04)",
                color: "var(--text-primary)",
                fontSize: 14,
                lineHeight: "21px",
                fontFamily: "inherit",
                outline: "none",
                boxSizing: "border-box",
              }}
            />
            <div>
              <ActionButton
                label={
                  sendMessageMutation.isPending
                    ? t("jobApplicants.actions.sendingMessage")
                    : t("jobApplicants.actions.sendMessage")
                }
                onClick={handleSendMessage}
                disabled={!message.trim() || sendMessageMutation.isPending}
                loading={sendMessageMutation.isPending}
                icon={<MaterialSymbol name="send" size={16} />}
              />
            </div>
          </div>
        </SectionCard>

        {/* Status history */}
        <StatusHistorySection
          title={t("jobApplicants.sections.statusHistory")}
          entries={detail.statusHistory}
          isRTL={isRTL}
          locale={i18n.language}
          byUserLabel={(id) => t("jobApplicants.fields.byUserId", { id })}
          statusLabel={(status) =>
            t(`jobApplicants.status.${status}` as never) as string
          }
        />

        {/* Contact */}
        <SectionCard title={t("jobApplicants.sections.contact")}>
          <FactGrid facts={contactItems} />
        </SectionCard>

        {/* Application meta */}
        <SectionCard title={t("jobApplicants.sections.actions")}>
          <FactGrid facts={factItems} />
        </SectionCard>

        {/* Profile + skills */}
        <SectionCard title={t("jobApplicants.sections.profile")}>
          <div
            style={{
              fontSize: 14,
              lineHeight: "22px",
              color: "var(--text-secondary)",
              whiteSpace: "pre-wrap",
              textAlign: "start",
              padding: "0 8px",
            }}
          >
            {profileSummary ?? t("jobApplicants.noData.profile")}
          </div>

          {detail.candidateProfile.skills.length ? (
            <div
              style={{
                display: "flex",
                gap: 6,
                flexWrap: "wrap",
                marginTop: 16,
                padding: "0 8px",
              }}
            >
              {detail.candidateProfile.skills.map((skill) => (
                <span key={skill} className="tabbio-menu-badge">
                  {skill}
                </span>
              ))}
            </div>
          ) : null}
        </SectionCard>
      </div>
    </Frame>
  );
}

// ─── Talent submission detail (same family, minus job-only actions) ─────────

function TalentSubmissionDetailBody({
  submissionId,
  headerSlot,
  variant,
}: {
  submissionId: string;
  headerSlot?: ReactNode;
  variant: "page" | "pane";
}) {
  const { t, i18n } = useTranslation(["common", "employer"]);
  const { isRTL } = useLocale();

  const detailQuery = useTalentSubmissionDetail(submissionId);
  const detail = detailQuery.data;

  if (detailQuery.isLoading) {
    return (
      <Frame variant={variant} state="loading">
        <JobsSkeleton variant="detail" />
      </Frame>
    );
  }

  if (detailQuery.error || !detail) {
    return (
      <Frame variant={variant} state="error">
        <div className="tabbio-svc-page">
          {headerSlot}
          <CenterState
            title={
              detailQuery.error
                ? t("common:jobApplicants.loadErrorTitle")
                : t("common:jobApplicants.notFoundTitle")
            }
            subtitle={
              detailQuery.error
                ? t("common:jobApplicants.loadErrorSubtitle")
                : t("common:jobApplicants.notFoundSubtitle")
            }
            detail={detailQuery.error?.message ?? null}
            actionLabel={t("common:retry")}
            onAction={() => detailQuery.refetch()}
          />
        </div>
      </Frame>
    );
  }

  const destinationLabel = t("employer:applications.destinations.talentNetwork");
  const isWithdrawn = detail.status === "withdrawn";
  const statusLabel = isWithdrawn
    ? t("common:jobApplicants.status.withdrawn")
    : t("employer:applications.status.active");
  const cv: ApplicantCvPaperDocument | null = detail.submittedCvDocument;
  const profileSummary =
    detail.candidateProfile?.about || detail.candidateProfile?.bio || null;
  const skills = detail.candidateProfile?.skills ?? [];

  const factItems = [
    {
      label: t("employer:applications.detail.destinationLabel"),
      value: destinationLabel,
    },
    {
      label: t("employer:applications.detail.submittedLabel"),
      value: formatAbsoluteDate(detail.submittedAt, i18n.language),
    },
    {
      label: t("common:jobApplicants.fields.updatedAt"),
      value: formatAbsoluteDate(detail.updatedAt, i18n.language),
    },
    {
      label: t("employer:applications.detail.statusLabel"),
      value: statusLabel,
    },
  ];

  const contactItems = [
    {
      label: t("common:jobApplicants.fields.location"),
      value:
        detail.candidate.location || t("common:jobApplicants.noDataValue"),
    },
    {
      label: t("common:jobApplicants.fields.email"),
      value:
        detail.candidate.contactEmail ||
        detail.candidate.email ||
        t("common:jobApplicants.noDataValue"),
    },
    {
      label: t("common:jobApplicants.fields.phone"),
      value: detail.candidate.phone || t("common:jobApplicants.noDataValue"),
      dir: 'ltr' as const,
    },
  ];

  return (
    <Frame variant={variant} state="content">
      <div
        dir={isRTL ? "rtl" : "ltr"}
        className="tabbio-svc-page"
        style={{
          display: "flex",
          flexDirection: "column",
          gap: 32,
        }}
      >
        {headerSlot}

        {/* Hero */}
        <header style={{ display: "flex", flexDirection: "column", gap: 18 }}>
          <div
            style={{
              display: "flex",
              alignItems: "flex-start",
              gap: 18,
              minWidth: 0,
            }}
          >
            <Avatar
              name={detail.candidate.name}
              imageUrl={detail.candidate.image}
              size={64}
            />

            <div
              style={{
                flex: 1,
                minWidth: 0,
                display: "flex",
                flexDirection: "column",
                gap: 8,
              }}
            >
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 8,
                  flexWrap: "wrap",
                }}
              >
                <StatusBadge label={statusLabel} />
                <InfoPill
                  icon={
                    <MaterialSymbol
                      name="schedule"
                      size={14}
                      style={{ color: "var(--text-tertiary)" }}
                    />
                  }
                  label={t("employer:applications.submittedAgo", {
                    time: formatRelativeDate(
                      detail.submittedAt,
                      i18n.language,
                    ),
                  })}
                />
              </div>

              <h1
                style={{
                  margin: 0,
                  fontFamily: "'Google Sans', sans-serif",
                  fontSize: 32,
                  lineHeight: "38px",
                  fontWeight: 400,
                  letterSpacing: "-0.01em",
                  color: "var(--text-primary)",
                }}
                dir="auto"
              >
                {detail.candidate.name}
              </h1>

              <div
                style={{
                  fontSize: 15,
                  lineHeight: "22px",
                  color: "var(--text-secondary)",
                }}
              >
                {[
                  detail.candidate.title ||
                    t("common:jobApplicants.noTitle"),
                  destinationLabel,
                ]
                  .filter(Boolean)
                  .join(" · ")}
              </div>
            </div>
          </div>

          {/* Contact pills (consent-gated server-side) */}
          <div
            style={{
              display: "flex",
              gap: 8,
              flexWrap: "wrap",
            }}
          >
            {detail.candidate.location ? (
              <InfoPill
                icon={
                  <MaterialSymbol
                    name="location_on"
                    size={14}
                    style={{ color: "var(--text-tertiary)" }}
                  />
                }
                label={detail.candidate.location}
              />
            ) : null}
            {detail.candidate.contactEmail || detail.candidate.email ? (
              <InfoPill
                icon={
                  <MaterialSymbol
                    name="mail"
                    size={14}
                    style={{ color: "var(--text-tertiary)" }}
                  />
                }
                label={detail.candidate.contactEmail || detail.candidate.email}
              />
            ) : null}
            {detail.candidate.phone ? (
              <InfoPill
                icon={
                  <MaterialSymbol
                    name="call"
                    size={14}
                    style={{ color: "var(--text-tertiary)" }}
                  />
                }
                label={detail.candidate.phone}
                dir="ltr"
              />
            ) : null}
            <InfoPill
              icon={
                <MaterialSymbol
                  name="hub"
                  size={14}
                  style={{ color: "var(--text-tertiary)" }}
                />
              }
              label={destinationLabel}
            />
          </div>

          {isWithdrawn ? (
            <div
              style={{
                padding: "14px 18px",
                borderRadius: 18,
                backgroundColor: "rgba(0,0,0,0.04)",
                fontSize: 13,
                lineHeight: "20px",
                color: "var(--text-secondary)",
                textAlign: "start",
              }}
            >
              {t("employer:applications.detail.withdrawnNotice")}
            </div>
          ) : null}

          {profileSummary ? (
            <div
              style={{
                marginTop: 4,
                fontSize: 14,
                lineHeight: "22px",
                color: "var(--text-secondary)",
                whiteSpace: "pre-wrap",
                textAlign: "start",
              }}
            >
              {profileSummary}
            </div>
          ) : null}
        </header>

        {/* Submitted CV */}
        <SectionCard
          title={
            cv?.snapshot === false
              ? t("employer:applications.currentCvNotice")
              : t("employer:applications.submittedCv")
          }
        >
          {cv ? (
            <EmployerApplicantCvPaper
              detail={detail}
              cv={cv}
              locale={i18n.language}
              isRTL={isRTL}
            />
          ) : (
            <div
              style={{
                padding: "24px 18px",
                borderRadius: 20,
                backgroundColor: "rgba(0,0,0,0.04)",
                fontSize: 14,
                lineHeight: "21px",
                color: "var(--text-secondary)",
                textAlign: "center",
              }}
            >
              {t("common:jobApplicants.cv.empty")}
            </div>
          )}
        </SectionCard>

        {/* Every submission of this membership, as summaries (E03): a
            withdrawn and resubmitted membership lists both. */}
        <SubmissionReceiptsSection audience="employer" fields={detail} />

        {/* Consent */}
        <SectionCard title={t("employer:applications.detail.consentTitle")}>
          <div
            style={{
              display: "flex",
              flexDirection: "column",
              gap: 6,
              padding: "16px 18px",
              borderRadius: 20,
              backgroundColor: "rgba(0,0,0,0.04)",
              textAlign: "start",
            }}
          >
            <div
              style={{
                fontSize: 14,
                lineHeight: "21px",
                color: "var(--text-secondary)",
                whiteSpace: "pre-wrap",
              }}
            >
              {detail.consent?.summary ||
                t("employer:applications.detail.consentBody")}
            </div>
            {detail.consent?.acceptedAt ? (
              <div
                style={{
                  fontSize: 12,
                  lineHeight: "18px",
                  color: "var(--text-tertiary)",
                }}
              >
                {t("employer:applications.detail.consentAcceptedAt", {
                  date: formatAbsoluteDate(
                    detail.consent.acceptedAt,
                    i18n.language,
                  ),
                })}
              </div>
            ) : null}
          </div>
        </SectionCard>

        {/* Status history */}
        <StatusHistorySection
          title={t("common:jobApplicants.sections.statusHistory")}
          entries={detail.statusHistory}
          isRTL={isRTL}
          locale={i18n.language}
          byUserLabel={(id) =>
            t("common:jobApplicants.fields.byUserId", { id }) as string
          }
          statusLabel={(status) =>
            status === "active"
              ? (t("employer:applications.status.active") as string)
              : status === "withdrawn"
                ? (t("common:jobApplicants.status.withdrawn") as string)
                : status
          }
        />

        {/* Contact */}
        <SectionCard title={t("common:jobApplicants.sections.contact")}>
          <FactGrid facts={contactItems} />
        </SectionCard>

        {/* Submission meta */}
        <SectionCard title={t("employer:applications.detail.talentTitle")}>
          <FactGrid facts={factItems} />
        </SectionCard>

        {/* Skills */}
        {skills.length ? (
          <SectionCard title={t("common:jobApplicants.sections.skills")}>
            <div
              style={{
                display: "flex",
                gap: 6,
                flexWrap: "wrap",
                padding: "0 8px",
              }}
            >
              {skills.map((skill) => (
                <span key={skill} className="tabbio-menu-badge">
                  {skill}
                </span>
              ))}
            </div>
          </SectionCard>
        ) : null}
      </div>
    </Frame>
  );
}

// ─── Public entry ───────────────────────────────────────────────────────────

export function ApplicantDetailBody({
  jobId,
  applicationId,
  submissionId,
  headerSlot,
  onStatusChanged,
  variant = "page",
}: ApplicantDetailBodyProps) {
  const { t } = useTranslation("common");

  if (submissionId) {
    return (
      <TalentSubmissionDetailBody
        submissionId={submissionId}
        headerSlot={headerSlot}
        variant={variant}
      />
    );
  }

  if (applicationId && jobId) {
    return (
      <ApplicationDetailBody
        jobId={jobId}
        applicationId={applicationId}
        headerSlot={headerSlot}
        onStatusChanged={onStatusChanged}
        variant={variant}
      />
    );
  }

  return (
    <Frame variant={variant} state="error">
      <div className="tabbio-svc-page">
        {headerSlot}
        <CenterState
          title={t("jobApplicants.notFoundTitle")}
          subtitle={t("jobApplicants.notFoundSubtitle")}
        />
      </div>
    </Frame>
  );
}
